import { flatten, range } from 'lodash';
import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import { handleError } from 'spw-redux/modules/UI/messages/actions';
import { APIResponse } from 'spw-redux/types';
import { SortingDirection } from 'spw-components/collab/types';
import { getUser, listUsers } from 'spw-api';
import { getBenchmarkReportsApi } from 'spw-api/benchmark-reports';
import * as usersActions from './actions';
import {
  getProjectsForUser,
  getTeamsForUser,
  assignUserAsOrgAdmin as assignUserAsOrgAdminAPI,
} from '../../../api/users';
import { selectCurrentOrganization } from '../organization/selectors';
import { Organization } from '../organization/types';
import { USER_PROJECT_ITEMS_PER_PAGE } from '../../../users/user-detail/user-projects-table';
import { selectProjectsForSelectedUserList } from './selectors';

function* fetchUsers(action: ReturnType<typeof usersActions.fetchUsers>) {
  try {
    const { sortBy, filterValues, page, itemsPerPage } = action.payload;
    const currentOrg: Organization = yield select(selectCurrentOrganization);

    const response: APIResponse<typeof listUsers> = yield call(
      listUsers,
      page,
      itemsPerPage,
      { ...filterValues, organizationId: currentOrg?.id },
      sortBy
        ? {
            [sortBy.column]: sortBy.direction.toUpperCase() as SortingDirection,
          }
        : {}
    );

    yield put(
      usersActions.actions.fetchUsersSucceeded(response.data.listUsers)
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getSelectedUserCall(
  action: ReturnType<typeof usersActions.getSelectedUser>
) {
  try {
    const response: APIResponse<typeof getUser> = yield call(
      getUser,
      action.payload
    );

    yield put(
      usersActions.actions.getSelectedUserSucceeded(response.data.user)
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getTeamsForSelectedUserCall(
  action: ReturnType<typeof usersActions.getTeamsForSelectedUser>
) {
  try {
    const response: APIResponse<typeof getTeamsForUser> = yield call(
      getTeamsForUser,
      action.payload
    );
    yield put(
      usersActions.actions.getTeamsForSelectedUserSucceeded(
        response.data.results
      )
    );
  } catch (e) {
    yield put(handleError(e));
    yield put(usersActions.actions.getTeamsForSelectedUserFailed());
  }
}

function* getProjectsForSelectedUserCall(
  action: ReturnType<typeof usersActions.getProjectsForSelectedUser>
) {
  try {
    const { userId, sort, page } = action.payload;
    const response: APIResponse<typeof getProjectsForUser> = yield call(
      getProjectsForUser,
      userId,
      sort,
      USER_PROJECT_ITEMS_PER_PAGE,
      page || 1
    );

    const {
      data: { editableProjects },
    } = response;

    yield put(
      usersActions.actions.getProjectsForSelectedUserSucceeded({
        ...editableProjects,
        lastSortUsed: sort,
      })
    );
  } catch (e) {
    yield put(handleError(e));
    yield put(usersActions.actions.getProjectsForSelectedUserFailed());
  }
}

function* getReportsForSelectedUserCall(
  action: ReturnType<typeof usersActions.getReportsForSelectedUser>
) {
  try {
    const response: APIResponse<typeof getBenchmarkReportsApi> = yield call(
      getBenchmarkReportsApi,
      action.payload
    );

    const {
      data: { paginatedReports },
    } = response;

    yield put(
      usersActions.actions.getReportsForSelectedUserSucceeded(paginatedReports)
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* refetchProjectsForSelectedUserStartingFromPage(
  action: ReturnType<
    typeof usersActions.refetchProjectsForSelectedUserStartingFromPage
  >
) {
  try {
    const { userId, startingPage } = action.payload;
    const {
      items,
      lastSortUsed,
    }: ReturnType<typeof selectProjectsForSelectedUserList> = yield select(
      selectProjectsForSelectedUserList
    );
    const lastFetchedPage = items.length / USER_PROJECT_ITEMS_PER_PAGE;

    const responses: APIResponse<typeof getProjectsForUser>[] = yield all(
      range(startingPage, lastFetchedPage + 1).map((page) =>
        call(
          getProjectsForUser,
          userId,
          lastSortUsed,
          USER_PROJECT_ITEMS_PER_PAGE,
          page
        )
      )
    );

    const projects = flatten(
      responses.map((r) => r.data.editableProjects.projects)
    );
    const { total } = responses[0].data.editableProjects;

    yield put(
      usersActions.actions.refetchProjectsForSelectedUserStartingFromPageSucceeded(
        {
          projects,
          total,
          startingPage,
        }
      )
    );
  } catch (e) {
    yield put(handleError(e));
    yield put(
      usersActions.actions.refetchProjectsForSelectedUserStartingFromPageFailed()
    );
  }
}

function* assignUserAsOrgAdmin(
  action: ReturnType<typeof usersActions.assignUserAsOrgAdmin>
) {
  try {
    yield call(assignUserAsOrgAdminAPI, action.payload.user);

    yield put(
      usersActions.assignUserAsOrgAdminSucceeded(action.payload.user.isOrgAdmin)
    );

    yield call(action.payload.onSuccess);
  } catch (e) {
    yield put(usersActions.assignUserAsOrgAdminFailed());
    yield put(handleError(e));
  }
}

function* saga() {
  yield all([
    takeLatest(usersActions.FETCH_USERS, fetchUsers),
    takeLatest(usersActions.GET_SELECTED_USER, getSelectedUserCall),
    takeLatest(
      usersActions.GET_TEAMS_FOR_SELECTED_USER,
      getTeamsForSelectedUserCall
    ),
    takeLatest(
      usersActions.GET_PROJECTS_FOR_SELECTED_USER,
      getProjectsForSelectedUserCall
    ),
    takeLatest(
      usersActions.REFETCH_PROJECTS_FOR_SELECTED_USER_STARTING_FROM_PAGE,
      refetchProjectsForSelectedUserStartingFromPage
    ),
    takeLatest(usersActions.ASSIGN_USER_AS_ORG_ADMIN, assignUserAsOrgAdmin),
    takeLatest(
      usersActions.GET_REPORTS_FOR_SELECTED_USER,
      getReportsForSelectedUserCall
    ),
  ]);
}

export default saga;
